"use client";

import { useState, type FormEvent } from "react";
import { FiArrowUpRight, FiCheck, FiMapPin, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import { KIND_LABELS, LANDMARK_KINDS, type Landmark, type LandmarkInput, type NearbyRoad, type TravelDirection } from "@/lib/navigation/types";
import cls from "./Navigation.module.css";

interface Props {
    draft: LandmarkInput;
    selected: Landmark | null;
    canEdit: boolean;
    saving: boolean;
    dirty: boolean;
    nearby: NearbyRoad[];
    nearbyLoading: boolean;
    nearbyError: string;
    error: string;
    onChange: (patch: Partial<LandmarkInput>) => void;
    onClose: () => void;
    onMove: () => void;
    onSave: () => void;
    onDelete: () => void;
    onUnlock: () => void;
}

const angle = (bearing: number) => `${Math.round((bearing + 360) % 360)}°`;

export function LandmarkEditor(props: Props) {
    const { draft, selected, nearby, saving, canEdit, onChange } = props;
    const [candidate, setCandidate] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const available = nearby.filter(road => !draft.bindings.some(binding => binding.edgeId === road.id));
    const candidateId = available.some(road => road.id === candidate) ? candidate : available[0]?.id ?? "";
    const sourceNode = selected?.sourceRef?.match(/^node\/(\d+)\//)?.[1];

    function addBinding() {
        const road = available.find(road => road.id === candidateId);
        if (!road) return;
        onChange({ bindings: [...draft.bindings, {
            edgeId: road.id, direction: road.oneWay === -1 ? "backward" : "forward",
        }] });
    }

    function submit(event: FormEvent) {
        event.preventDefault();
        if (!saving && canEdit) props.onSave();
    }

    return <div className={cls.editor}>
        <div className={cls.panelHeading}>
            <div><p className={cls.eyebrow}>{selected ? "Картка орієнтира" : "Новий орієнтир"}</p>
                <h2>{KIND_LABELS[draft.kind]}</h2></div>
            <button type="button" className={cls.iconButton} onClick={props.onClose} disabled={saving} aria-label="Закрити редактор"><FiX /></button>
        </div>
        {props.dirty && <span className={cls.unsaved}>Є незбережені зміни</span>}
        {!canEdit && <div className={cls.notice}>
            <p>Відкрито для перегляду. Для змін потрібен ключ редактора.</p>
            <button type="button" className={cls.textButton} onClick={props.onUnlock}>Увести ключ <FiArrowUpRight /></button>
        </div>}

        <form onSubmit={submit}>
            <fieldset disabled={saving || !canEdit} className={cls.fields}>
                <label className={cls.field}>Тип об’єкта
                    <select value={draft.kind} onChange={event => onChange({ kind: event.target.value as LandmarkInput["kind"] })}>
                        {LANDMARK_KINDS.map(kind => <option key={kind} value={kind}>{KIND_LABELS[kind]}</option>)}
                    </select>
                </label>
                <label className={cls.field}>Назва <span className={cls.optional}>необов’язково</span>
                    <input value={draft.title} maxLength={160} onChange={event => onChange({ title: event.target.value })} placeholder="Наприклад, світлофор біля метро" />
                </label>
                {draft.kind === "road_sign" && <div className={cls.fieldRow}>
                    <label className={cls.field}>Код знака
                        <input required value={draft.signCode} maxLength={40} onChange={event => onChange({ signCode: event.target.value })} placeholder="3.29" />
                    </label>
                    <label className={cls.field}>Значення
                        <input value={draft.signValue} maxLength={60} onChange={event => onChange({ signValue: event.target.value })} placeholder="50 км/год" />
                    </label>
                </div>}

                <div className={cls.formSection}>
                    <h3><FiMapPin /> Розташування</h3>
                    <div className={cls.fieldRow}>
                        <label className={cls.field}>Широта
                            <input required type="number" step="any" min="50.15" max="50.75" value={Number.isFinite(draft.latitude) ? draft.latitude : ""}
                                onChange={event => onChange({ latitude: event.target.value === "" ? NaN : Number(event.target.value) })} />
                        </label>
                        <label className={cls.field}>Довгота
                            <input required type="number" step="any" min="30.15" max="30.95" value={Number.isFinite(draft.longitude) ? draft.longitude : ""}
                                onChange={event => onChange({ longitude: event.target.value === "" ? NaN : Number(event.target.value) })} />
                        </label>
                    </div>
                    <button type="button" className={cls.secondaryButton} onClick={props.onMove}><FiMapPin /> Указати на карті</button>
                    <p className={cls.hint}>Маркер можна перетягнути. Переміщення скидає прив’язки до доріг і статус перевірки.</p>
                    <div className={cls.fieldRow}>
                        <label className={cls.field}>Азимут руху, °
                            <input type="number" min="0" max="359.99" step="any" value={draft.viewHeading ?? ""}
                                onChange={event => onChange({ viewHeading: event.target.value === "" ? null : Number(event.target.value) })} placeholder="Невідомо" />
                        </label>
                        <label className={cls.field}>Похибка, м
                            <input type="number" min="0.1" max="1000" step="any" value={draft.accuracyMeters ?? ""}
                                onChange={event => onChange({ accuracyMeters: event.target.value === "" ? null : Number(event.target.value) })} placeholder="Невідомо" />
                        </label>
                    </div>
                    <p className={cls.hint}>Азимут напрямку руху, з якого видно об’єкт: 0° — північ, 90° — схід. Невідомі значення залишайте порожніми.</p>
                </div>

                <div className={cls.formSection}>
                    <h3><FiArrowUpRight /> Прив’язка до дороги <span>{draft.bindings.length}/6</span></h3>
                    <p className={cls.hint}>Наблизьте карту й натисніть на ділянку або виберіть зі списку в радіусі 100 м.</p>
                    {draft.bindings.map(binding => {
                        const road = nearby.find(road => road.id === binding.edgeId);
                        const saved = selected?.bindings.find(item => item.edgeId === binding.edgeId);
                        return <div className={cls.binding} key={binding.edgeId}>
                            <div className={cls.bindingHeading}>
                                <strong>{road?.name || saved?.roadName || "Ділянка без назви"}</strong>
                                <button type="button" className={cls.iconButton} aria-label="Прибрати прив’язку" onClick={() => onChange({ bindings: draft.bindings.filter(item => item.edgeId !== binding.edgeId) })}><FiX /></button>
                            </div>
                            <span className={cls.hint}>{binding.edgeId}{road ? ` · ${Math.round(road.distanceMeters)} м від об’єкта` : ""}</span>
                            {saved?.active === false && <p className={cls.fieldError}>Цю ділянку змінено в OSM. Виберіть актуальну прив’язку.</p>}
                            <label className={cls.field}>Напрямок спостереження
                                <select value={binding.direction} onChange={event => onChange({ bindings: draft.bindings.map(item => item.edgeId === binding.edgeId
                                    ? { ...item, direction: event.target.value as TravelDirection } : item) })}>
                                    <option value="forward" disabled={road?.oneWay === -1}>За стрілкою{road ? ` · ${angle(road.bearing)}` : ""}</option>
                                    <option value="backward" disabled={road?.oneWay === 1}>Проти стрілки{road ? ` · ${angle(road.bearing + 180)}` : ""}</option>
                                    <option value="both" disabled={!!road?.oneWay}>Обидва напрямки</option>
                                </select>
                            </label>
                        </div>;
                    })}
                    {props.nearbyLoading && <p className={cls.hint} role="status">Шукаємо ділянки поруч…</p>}
                    {props.nearbyError && <p className={cls.fieldError}>{props.nearbyError}</p>}
                    {!props.nearbyLoading && !props.nearbyError && !nearby.length && <p className={cls.hint}>У радіусі 100 м немає імпортованих доріг. Можна зберегти орієнтир без прив’язки.</p>}
                    {available.length > 0 && draft.bindings.length < 6 && <>
                        <label className={cls.field}>Ділянка поруч
                            <select value={candidateId} onChange={event => setCandidate(event.target.value)}>
                                {available.map(road => <option key={road.id} value={road.id}>
                                    {road.name || "Без назви"} · {Math.round(road.distanceMeters)} м · {angle(road.bearing)} · {Math.round(road.lengthMeters)} м завдовжки
                                </option>)}
                            </select>
                        </label>
                        <button type="button" className={cls.secondaryButton} onClick={addBinding}><FiPlus /> Додати прив’язку</button>
                    </>}
                    <p className={cls.hint}>Стрілка на вибраній ділянці показує напрямок її геометрії. Двосторонні дороги допускають обидва напрямки спостереження.</p>
                </div>

                <label className={cls.field}>Нотатки
                    <textarea rows={3} value={draft.notes} maxLength={2000} onChange={event => onChange({ notes: event.target.value })} placeholder="Сторона дороги, орієнтири поруч, що потрібно уточнити…" />
                </label>
                <label className={cls.checkField}>
                    <input type="checkbox" checked={draft.verified} onChange={event => onChange({ verified: event.target.checked })} />
                    <span><strong>Перевірено</strong><small>Координати й властивості уточнені за актуальним фото або на місці.</small></span>
                </label>
            </fieldset>

            {selected && <div className={cls.sourceInfo}>
                <span>Джерело: {selected.source === "osm" ? "OpenStreetMap" : "Ручна розмітка"} · версія {selected.version}</span>
                {sourceNode && <a href={`https://www.openstreetmap.org/node/${sourceNode}`} target="_blank" rel="noopener noreferrer">Відкрити в OSM <FiArrowUpRight /></a>}
                {selected.source === "osm" && <p>OSM може позначати місце регулювання руху, а не фізичний пристрій. Перевірте положення маркера.</p>}
            </div>}
            {props.error && <p className={cls.formError} role="alert">{props.error}</p>}
            {canEdit && <div className={cls.formActions}>
                <button type="submit" className={cls.primaryButton} disabled={saving || !props.dirty}>
                    <FiCheck /> {saving ? "Зберігаємо…" : "Зберегти орієнтир"}
                </button>
                {selected && !confirmDelete && <button type="button" className={cls.deleteButton} disabled={saving} onClick={() => setConfirmDelete(true)}><FiTrash2 /> Видалити</button>}
                {selected && confirmDelete && <div className={cls.deleteConfirm}>
                    <p>Видалити цей орієнтир із карти?</p>
                    <button type="button" className={cls.deleteButton} disabled={saving} onClick={props.onDelete}>Так, видалити</button>
                    <button type="button" className={cls.textButton} disabled={saving} onClick={() => setConfirmDelete(false)}>Скасувати</button>
                </div>}
            </div>}
        </form>
    </div>;
}
